import axios, { AxiosInstance, AxiosProgressEvent, AxiosResponse } from "axios";
import { ApiRoutes } from "../apiConfig/ApiRoutes";
import { msalAuthenticator } from "../accounts/auth";
import { BlobResult } from "./BlobResult";

const responseBody = (response: AxiosResponse) => response.data;

/**
 * Handles async http requests to given end point with json serialisation
 */
export default class HttpEndpoint {

  private axiosInstance: AxiosInstance;

  /**
   * 
   * @param baseUrl Url of the api endpoint
   */
  constructor() {
   
    this.axiosInstance = axios.create({ baseURL: ApiRoutes.API_BASE_URL });

    //configure access token
    this.axiosInstance.interceptors.request.use(
        async (config) => {
            const token = await this.getAccessToken();
            if (token) {
                config.headers["Authorization"] = `Bearer ${token}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );
  }

  async getAccessToken() : Promise<string | undefined> {
    const token = await msalAuthenticator.getToken();
    if (!token) {
        throw new Error("No token acquired.");
    }
    return token;
  }

  /**
   * Send a GET request to the endpoint at given sub path
   * @param path sub path of the resource
   * @param params url parameters
   * @returns Body of the response deserialised from json as T
   * @throws {Error} when request failed or responses status is not 200 
   */
  async get<T>(path: string, params?: any): Promise<T> {
    
    
    return this.axiosInstance.get(path, {
      params: params
    })
    .then(responseBody);
  }

  async getBlobUrl<T>(path: string, params?: any): Promise<BlobResult> {
    
    return this.axiosInstance.get(path, {
      params: params,
      responseType: 'blob'
    })
    .then(response => {
      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], {type: contentType });
       
      const url = URL.createObjectURL(blob);
      return { url, mimeType: contentType };
    });
  }

  /**
   * Send a POST request to the endpoint at given sub path
   * @param data the data to include in the body as json
   * @param path sub path of the resource
   * @param params url parameters
   * @returns Body of the response deserialised from json as T
   * @throws {Error} when request failed or responses status is not 201
   */
  async post<T>(
    path: string, 
    data: any, 
    params?: any | undefined, 
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void): Promise<T> {
    
    return this.axiosInstance.post(path, data, {
      params: params,
      onUploadProgress: onUploadProgress
    })
    .then(responseBody);
  }
  
  /**
   * Send a PUT request to the endpoint at given sub path
   * @param data the data to include in the body as json
   * @param path sub path of the resource
   * @param params url parameters
   * @returns Body of the response deserialised from json as T
   * @throws {Error} when request failed or responses status is not 200 
   */
  async put<T>(path: string, data: any, params?: any | undefined): Promise<T> {
    
    const response = await this.axiosInstance.put(path, data, {
      params: params
    });
    
    return response.data as T;
  }

  async patch<T>(path: string, data: any, params?: any | undefined): Promise<T> {
    
    const response = await this.axiosInstance.patch(path, data, {
      params: params
    });
    
    return response.data as T;
  }
  
  /**
   * Send a DELETE request to the endpoint at given sub path
   * @param path sub path of the resource
   * @param params url parameters
   * @returns Body of the response deserialised from json as T
   * @throws {Error} when request failed or responses status is not 200 
   */
  async delete<T>(path: string, params?: any | undefined): Promise<T> {
    
    const response = await this.axiosInstance.delete(path, {
      params: params
    });
    
    return response.data as T;
  }
}